import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const PostCreator = ({ onClose, onPostCreated }) => {
  const [postData, setPostData] = useState({
    post_type: 'text',
    title: '',
    content: '',
    location: '',
    tags: [],
    visibility: 'public'
  });
  const [loading, setLoading] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const postTypes = [
    { key: 'text', label: 'Texte', icon: 'FileText', description: 'Partager une réflexion ou une actualité' },
    { key: 'achievement', label: 'Réussite', icon: 'Trophy', description: 'Célébrer un accomplissement' },
    { key: 'performance_announcement', label: 'Performance', icon: 'Music', description: 'Annoncer un concert ou événement' },
    { key: 'collaboration', label: 'Collaboration', icon: 'Handshake', description: 'Chercher ou annoncer une collaboration' },
    { key: 'career_milestone', label: 'Étape Carrière', icon: 'Star', description: 'Partager une évolution professionnelle' }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!postData?.content?.trim()) {
      return;
    }

    try {
      setLoading(true);

      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) {
        throw new Error('Vous devez être connecté pour publier');
      }

      const { data, error } = await supabase?.from('professional_posts')?.insert({
          author_id: session?.user?.id,
          post_type: postData?.post_type,
          title: postData?.title?.trim() || null,
          content: postData?.content?.trim(),
          location: postData?.location?.trim() || null,
          tags: postData?.tags?.length > 0 ? postData?.tags : null,
          visibility: postData?.visibility
        })?.select()?.single();

      if (error) throw error;

      onPostCreated?.(data);
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Erreur lors de la publication. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    const tag = tagInput?.trim()?.toLowerCase();
    if (tag && !postData?.tags?.includes(tag) && postData?.tags?.length < 10) {
      setPostData(prev => ({
        ...prev,
        tags: [...(prev?.tags || []), tag]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setPostData(prev => ({
      ...prev,
      tags: prev?.tags?.filter(tag => tag !== tagToRemove) || []
    }));
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      addTag();
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-surface rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Créer une publication</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="hover:bg-muted"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Post Type Selection */}
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              Type de publication
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {postTypes?.map((type) => (
                <div
                  key={type?.key}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    postData?.post_type === type?.key
                      ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50'
                  }`}
                  onClick={() => setPostData(prev => ({ ...prev, post_type: type?.key }))}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      postData?.post_type === type?.key ? 'bg-accent' : 'bg-muted'
                    }`}>
                      <Icon 
                        name={type?.icon} 
                        size={16} 
                        color={postData?.post_type === type?.key ? 'white' : undefined}
                      />
                    </div>
                    <div>
                      <div className="font-medium text-foreground text-sm">{type?.label}</div>
                      <div className="text-xs text-muted-foreground">{type?.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Title (optional for some post types) */}
          {(postData?.post_type === 'achievement' || postData?.post_type === 'performance_announcement' || postData?.post_type === 'career_milestone') && (
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Titre {postData?.post_type === 'achievement' ? '(optionnel)' : ''}
              </label>
              <Input
                placeholder="Titre de votre publication..."
                value={postData?.title}
                onChange={(e) => setPostData(prev => ({ ...prev, title: e?.target?.value }))}
                className="w-full"
              />
            </div>
          )}

          {/* Content */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Contenu *
            </label>
            <textarea
              placeholder="Que souhaitez-vous partager avec votre réseau ?"
              value={postData?.content}
              onChange={(e) => setPostData(prev => ({ ...prev, content: e?.target?.value }))}
              className="w-full min-h-32 p-3 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              maxLength={2000}
              required
            />
            <div className="text-xs text-muted-foreground text-right mt-1">
              {postData?.content?.length || 0}/2000 caractères
            </div>
          </div>

          {/* Location (for performance announcements) */}
          {postData?.post_type === 'performance_announcement' && (
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Lieu (optionnel)
              </label>
              <Input
                placeholder="Où aura lieu l'événement ?"
                value={postData?.location}
                onChange={(e) => setPostData(prev => ({ ...prev, location: e?.target?.value }))}
                className="w-full"
                iconName="MapPin"
              />
            </div>
          )}

          {/* Tags */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Tags (optionnel)
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {postData?.tags?.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 bg-accent/10 text-accent border border-accent/20 text-xs px-2 py-1 rounded-full"
                >
                  #{tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="hover:bg-accent/20 rounded-full p-0.5"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex space-x-2">
              <Input
                placeholder="Ajouter un tag..."
                value={tagInput}
                onChange={(e) => setTagInput(e?.target?.value)}
                onKeyPress={handleKeyPress}
                className="flex-1"
                disabled={postData?.tags?.length >= 10}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addTag}
                disabled={!tagInput?.trim() || postData?.tags?.length >= 10}
              >
                Ajouter
              </Button>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {postData?.tags?.length || 0}/10 tags
            </div>
          </div>

          {/* Visibility */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Visibilité
            </label>
            <div className="space-y-2">
              {[
                { key: 'public', label: 'Public', description: 'Visible par tous les utilisateurs' },
                { key: 'connections', label: 'Connexions', description: 'Visible par vos connexions uniquement' },
                { key: 'private', label: 'Privé', description: 'Visible par vous uniquement' }
              ]?.map((option) => (
                <label
                  key={option?.key}
                  className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    postData?.visibility === option?.key
                      ? 'border-accent bg-accent/10' :'border-border hover:bg-muted'
                  }`}
                >
                  <input
                    type="radio"
                    name="visibility"
                    value={option?.key}
                    checked={postData?.visibility === option?.key}
                    onChange={(e) => setPostData(prev => ({ ...prev, visibility: e?.target?.value }))}
                    className="w-4 h-4 text-accent"
                  />
                  <div>
                    <div className="font-medium text-foreground text-sm">{option?.label}</div>
                    <div className="text-xs text-muted-foreground">{option?.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={!postData?.content?.trim() || loading}
            >
              Publier
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostCreator;